/**
 * @file Defines functions to create retry config values.
 *
 * We use this pattern of development to allow us to leverage generated parsers,
 * for example the RateLimitExceededException we use in the sleep time determination.
 */
import { ApiResultError } from '@codegrade/api-core';
export { ApiResult, ApiResultError, WarningHeader } from '@codegrade/api-core';
import moment from 'moment';
import { RateLimitExceededException } from '@codegrade/api-models';
import { expr } from '@codegrade/utils';
/** Define the ground truth of which request methods are retryable, should be idempotent. */
const RETRYABLE_METHODS = new Set([
    'GET',
    'HEAD',
    'OPTIONS',
    'PUT',
    'DELETE',
]);
const DEFAULT_MAX_TRIES = 2;
const DEFAULT_MAX_SLEEP = moment.duration(30, 'seconds');
/**
 * Excludes aborted requests and malformed URL/path issues.
 *
 * @param error The error thrown by the transport.
 * @returns Whether a thrown error is retryable.
 */
function isRetryableError(error) {
    // biome-ignore lint/suspicious/noExplicitAny: Too difficult otherwise
    const type = error.type;
    // biome-ignore lint/suspicious/noExplicitAny: Too difficult otherwise
    const code = error.code;
    // biome-ignore lint/suspicious/noExplicitAny: Too difficult otherwise
    const message = error.message;
    return (type !== 'aborted' &&
        code !== 'ERR_UNESCAPED_CHARACTERS' &&
        message !== 'Request path contains unescaped characters');
}
/**
 * Build a `shouldRetry` policy that enforces a maximum number of attempts
 * and only retries idempotent methods on retryable transport errors.
 *
 * @param max The max number of total tries (including the first attempt).
 * @returns A `shouldRetry` config of shape `{ tag: 'custom', get }`.
 */
function createShouldRetryWithMax(max) {
    return {
        tag: 'custom',
        get: args => {
            const canStillRetry = args.amountOfFailedAttempts < max;
            if (!canStillRetry) {
                return false;
            }
            if (args.error instanceof ApiResultError) {
                if (args.error.error instanceof RateLimitExceededException) {
                    return true;
                }
                const status = args.error.response.status;
                const isRetryableStatus = (status >= 500 && status < 600) || status === 429;
                return RETRYABLE_METHODS.has(args.request.method) && isRetryableStatus;
            }
            return RETRYABLE_METHODS.has(args.request.method) && isRetryableError(args.error);
        },
    };
}
/**
 * Normalize the user-provided `shouldRetry` config into a callable policy.
 *
 * @param shouldRetry The optional `Config.shouldRetry` union from the public API.
 * @returns A `shouldRetry` config of shape `{ tag: 'custom', get }`.
 */
export function createShouldRetry(shouldRetry) {
    if (shouldRetry == null) {
        return createShouldRetryWithMax(DEFAULT_MAX_TRIES);
    }
    return expr.match(shouldRetry, 'tag', {
        capped: capped => createShouldRetryWithMax(capped.maxAmountOfTries),
        custom: custom => custom,
    });
}
/**
 * Create delay between attempts of requests using a max.
 *
 * @param max The maximum delay to apply between attempts.
 * @returns A `{ tag: 'custom', get }` delay function.
 */
function createDelayBetweenTriesWithMax(max) {
    return {
        tag: 'custom',
        get: async (args) => {
            // We know exactly how long to wait before we are allowed to retry.
            if (args.error instanceof ApiResultError &&
                args.error.error instanceof RateLimitExceededException) {
                return args.error.error.retryAfter;
            }
            // Calculate exponential backoff based on the try counter we are on.
            const exponentialBackoff = 2 ** args.amountOfFailedAttempts * 500;
            const jitter = exponentialBackoff * 0.2 * Math.random(); // 0-20% of the delay
            const sleepTime = moment.duration(exponentialBackoff + jitter);
            // Cap the sleep time on the maximum set.
            if (max.asMilliseconds() < sleepTime.asMilliseconds()) {
                return max.clone();
            }
            return sleepTime;
        },
    };
}
/**
 * Normalize `delayBetweenTries` config into a callable policy.
 *
 * @param delayBetweenTries Optional `Config.delayBetweenTries` that overrides defaults.
 * @returns A `delayBetweenTries` config of shape `{ tag: 'custom', get }`.
 */
export function createDelayBetweenTries(delayBetweenTries) {
    if (delayBetweenTries == null) {
        return createDelayBetweenTriesWithMax(DEFAULT_MAX_SLEEP);
    }
    return expr.match(delayBetweenTries, 'tag', {
        capped: capped => createDelayBetweenTriesWithMax(capped.maxDelayBetweenTries),
        custom: custom => custom,
    });
}
